/**
 *
 * AgendaDetail
 *
 */

import React, { memo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import { createStructuredSelector } from "reselect";
import { compose } from "redux";
import { Link, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

import { useInjectSaga } from "utils/injectSaga";
import { useInjectReducer } from "utils/injectReducer";
import reducer from "./reducer";
import saga from "./saga";

import { flushState, loadCourse, updateCourse } from "./actions";
import {
  makeSelectLoading,
  makeSelectError,
  makeSelectCourseDetail,
  // on date
  makeSelectOnDateAbsences,
  makeSelectOnDateAttendances,
} from "./selectors";

// antd component
import { Layout, Breadcrumb, Tooltip } from "antd";
const { Content } = Layout;

// assets
import CustomIcons from "components/CustomIcons";

// components
import CustomPageHeader from "components/CustomPageHeader";
import CourseCard from "mobile/components/CourseCard";
import ExcusedStudents from "mobile/components/ExcusedStudents";
// containers
import AttendanceControl from "mobile/pages/AttendanceControl";

// global user
import { withUser } from "engine/Contexts/User.context";
import { getObjId } from "utils/helpers/helper.general";
import { getFromAttendancesOnDate } from "utils/helpers/helper.generateData";
import { convertDateToString } from "utils/moment/moment.utils";

export function AgendaDetail({
  // main data
  courseDetail,
  onDateAbsences,
  onDateAttendances,
  // states
  user,
  history,
  error,
  loading,
  // actions
  loadCourse,
  updateCourse,
  flushState,
}) {
  useInjectReducer({ key: "agendaDetail", reducer });
  useInjectSaga({ key: "agendaDetail", saga });

  const { t } = useTranslation();

  const { id: initId } = useParams();
  const location = useLocation();

  const onDate = location?.state?.onDate;
  const timeZone = location?.state?.timeZone;
  const goBack = location?.state?.goBack;
  const goBackTitle = location?.state?.goBackTitle;

  const [breadcrumb, setBreadcrumb] = useState([
    {
      path: goBack || "/agenda",
      exact: true,
      breadcrumbName: goBackTitle || "Agenda",
    },
  ]);
  const [showAsProcessed, setShowAsProcessed] = useState(false);
  const [currentCourse, setCurrentCourse] = useState(courseDetail);
  const [currentAttendances, setCurrentAttendances] = useState(
    onDateAttendances || [],
  );

  // Clearing the state after unmounting a component
  // - work like ComponentDidUpdate
  useEffect(() => {
    return () => {
      // Anything in here is fired on component unmount.
      flushState();
      setBreadcrumb([]);
      setShowAsProcessed(false);
    };
  }, []);

  // Get Course Data
  useEffect(() => {
    initId && loadCourse(initId, onDate, timeZone);
    return () => {
      // Anything in here is fired on component unmount.
      flushState();
      setBreadcrumb([]);
      setShowAsProcessed(false);
    };
  }, [initId]);

  useEffect(() => {
    setCurrentAttendances(onDateAttendances || []);

    if (!!courseDetail?._id) {
      const attOnDate = getFromAttendancesOnDate(onDateAttendances, courseDetail, onDate);
      setShowAsProcessed(!!attOnDate?.length > 0);

      setCurrentCourse(courseDetail);

      const responsible = courseDetail?.responsible?.description
        ? `• ${courseDetail?.responsible?.description?.lastname} ${
          courseDetail?.responsible?.description?.firstname
          }`
        : "";
      const currentDate = !!onDate ? convertDateToString(onDate) : "";
      const courseTitle = `${courseDetail?.course_code ? ` • ${courseDetail?.course_code}` : ""} ${responsible} ${!!currentDate ? ` • ${currentDate}`:""}`;

      // Set Breadcrumbs
      setBreadcrumb([
        {
          path: goBack || "/agenda",
          exact: true,
          breadcrumbName: goBackTitle || "Agenda",
        },
        {
          path: `/agenda/${getObjId(courseDetail)}`,
          breadcrumbName: courseTitle,
        },
      ]);
    }
  }, [courseDetail]);

  const itemBreadcrumbRender = (route, params, routes, paths) => {
    const last = routes.indexOf(route) === routes.length - 1;
    return last ? (
      <span>{route.breadcrumbName}</span>
    ) : (
      <Link
        to={{
          pathname: route.path,
          state: {
            onDate,
            timeZone,
            from: "single_agenda",
            needRefresh: false,
          },
        }}
      >
        {route.breadcrumbName}
      </Link>
    );
  };


  // render function
  return (
    <Layout className="main-full-content">
      <Helmet>
        <title>{t("labels.course")}</title>
        <meta name="description" content="Description of Cours" />
      </Helmet>
      <Content className="main-content-container agenda-detail">
        {breadcrumb && (
          <CustomPageHeader
            className="agenda-detail__header"
            title={
              <>
                <Breadcrumb
                  className="absence-breadcrumb"
                  routes={breadcrumb}
                  itemRender={itemBreadcrumbRender}
                />
                {!!showAsProcessed && (
                  <Tooltip
                    placement="right"
                    trigger="hover"
                    color="#28a745"
                    title={t("attendances.controlScreenTitle")}
                    className="mark-tooltip"
                    getPopupContainer={trigger => trigger.parentElement}
                  >
                    <span className="agenda-detail-mark attendance-mark">
                      <CustomIcons type="registred" />
                    </span>
                  </Tooltip>
                )}
              </>
            }
            subTitlePosition="top"
          />
        )}

        <div className="agenda-detail__main">
          <CourseCard data={currentCourse} />
          <ExcusedStudents
            course={currentCourse}
            loading={loading}
            dataSource={currentCourse?.students || []}
            onDate={onDate}
            absences={onDateAbsences}
            attendances={currentAttendances}
          >
            <AttendanceControl
              mode="course"
              courseID={getObjId(currentCourse)}
              onDate={onDate}
              timeZone={timeZone}
              updateCourse={courseData => setCurrentCourse(courseData)}
              updateAttendances={attendanceData => {
                if (!!attendanceData) {
                  const hastAtt = currentAttendances?.find(
                    att => getObjId(att) == getObjId(attendanceData),
                  );
                  if (!!!hastAtt) {
                    const atts = [...currentAttendances, attendanceData];
                    const attOnDate = getFromAttendancesOnDate(
                      atts,
                      currentCourse,
                      onDate,
                    );
                    setShowAsProcessed(!!attOnDate?.length > 0);
                    setCurrentAttendances(atts);
                  } else {
                    const newAtts = currentAttendances?.map(att => {
                      if (getObjId(att) == getObjId(attendanceData)) {
                        return {
                          ...att,
                          ...attendanceData,
                        };
                      } else {
                        return att;
                      }
                    });
                    const attOnDate = getFromAttendancesOnDate(
                      newAtts,
                      currentCourse,
                      onDate,
                    );
                    setShowAsProcessed(!!attOnDate?.length > 0);
                    setCurrentAttendances([...newAtts]);
                  }
                }
              }}
            />
          </ExcusedStudents>
        </div>
      </Content>
    </Layout>
  );
}

AgendaDetail.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  loading: makeSelectLoading(),
  error: makeSelectError(),
  courseDetail: makeSelectCourseDetail(),
  onDateAbsences: makeSelectOnDateAbsences(),
  onDateAttendances: makeSelectOnDateAttendances(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushState()),
    loadCourse: (id, onDate, timeZone) =>
      dispatch(loadCourse(id, onDate, timeZone)),
    updateCourse: itemData => dispatch(updateCourse(itemData)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
  withUser,
)(AgendaDetail);
